<template>
  <div class="c-integration-workflow">
    <div class="workflow-enabled-setting">
      <div class="text-neutral-text-strong">
        <div class="workflow-title">
          <FontAwesomeIcon :icon="['fak', 'mapping']" />
          <h3>{{ title }}</h3>
        </div>
        <p class="workflow-description">
          {{ description }}
          <BittsLink
            v-if="hasLearnMore"
            text="Learn more."
            url="https://help.crossbeam.com/en/articles/11419695-partner-field-mapping-to-your-crm"
            :open-in-new-tab="true"
          />
        </p>
      </div>
      <component
        :is="showTooltip ? BittsTooltip : 'div'"
        data-testid="workflow-toggle"
      >
        <BittsSwitch
          v-model="isEnabled"
          :disabled="
            (title !== 'Enable Data Push' && !trayState.isEnabled) ||
            higherOrderError !== null ||
            trayState.isSaving ||
            trayState.isDeleting
          "
        />
        <template #title>
          Enable push data in General Settings for more granular push controls
        </template>
      </component>
    </div>
    <IndividualWorkflowStatus
      v-if="!isMainIntegrationToggle"
      :last-pushed="status?.last_completed_timestamp ?? null"
      :status="statusInPriorityOrder"
      :callout-text="calloutText"
    />
    <slot
      v-bind="{
        isSlotDisabled: statusInPriorityOrder === 'error' && !suppressSlotError,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsLink, BittsSwitch, BittsTooltip } from '@crossbeam/bitts';

import { computed, inject, toRef } from 'vue';

import useTrayStatus from '@/composables/useTrayStatus';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { TrayError, TrayStatus } from '@/types/integrations';

import IndividualWorkflowStatus from './IndividualWorkflowStatus.vue';

const { title, description, status, suppressSlotError } = defineProps<{
  title: string;
  description: string;
  status?: TrayStatus;
  hasLearnMore?: boolean;
  suppressSlotError?: boolean;
}>();

const isEnabled = defineModel<boolean>('isEnabled');

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;

const { higherOrderError, errorInfo, getCalloutText } = useTrayStatus(
  toRef(trayState.enabledTray),
);

const statusInPriorityOrder = computed(() => {
  if (higherOrderError.value) return errorInfo.value.calloutType;
  if (!isEnabled.value) return 'warning';
  if (status?.error?.error_type) return 'error';
  return 'success';
});

const calloutText = computed(() => {
  if (higherOrderError.value) return errorInfo.value.calloutText;
  if (!isEnabled.value) return '';
  return getCalloutText(status?.error?.error_type as TrayError);
});

const isMainIntegrationToggle = computed(() => title === 'Enable Data Push');

const showTooltip = computed(
  () => !isMainIntegrationToggle.value && !trayState.isEnabled,
);
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-integration-workflow {
  @apply p-16 flex flex-col gap-12;
}

.workflow-enabled-setting {
  @apply flex items-center gap-16;

  & > .text-neutral-text-strong {
    @apply flex-1 min-w-0;
  }
}

.workflow-title {
  @apply flex items-center text-neutral-text-strong font-bold gap-8 mb-4;
}

.workflow-description {
  @apply text-neutral-text text-sm;
}
</style>
